import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import {
  applyConsent,
  storeConsent,
  getStoredConsent,
  DEFAULT_CONSENT,
} from "@/lib/consent";

/**
 * Cookie consent banner with Google Consent Mode v2 update on user action.
 * Shown on first visit (when no consent is stored) or when opened from the footer.
 */
export default function CookieBanner({ openSignal, onClosed }) {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [consent, setConsent] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      // Reapply stored consent so gtag has the up-to-date state on every load.
      applyConsent(stored);
      setConsent(stored);
    }
  }, []);

  useEffect(() => {
    if (openSignal > 0) {
      setVisible(true);
      setShowPrefs(true);
    }
  }, [openSignal]);

  const acceptAll = () => {
    const next = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    setConsent(next);
    storeConsent(next);
    applyConsent(next);
    setVisible(false);
    setShowPrefs(false);
    onClosed && onClosed();
  };

  const rejectAll = () => {
    const next = { ...DEFAULT_CONSENT };
    setConsent(next);
    storeConsent(next);
    applyConsent(next);
    setVisible(false);
    setShowPrefs(false);
    onClosed && onClosed();
  };

  const savePrefs = () => {
    const next = { ...consent, necessary: true };
    storeConsent(next);
    applyConsent(next);
    setVisible(false);
    setShowPrefs(false);
    onClosed && onClosed();
  };

  const toggle = (key) => {
    setConsent((c) => ({ ...c, [key]: !c[key] }));
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6"
    >
      <div className="max-w-4xl mx-auto bg-slate-900 border border-slate-800 rounded-xl shadow-2xl p-6">
        {!showPrefs ? (
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg mb-2">
                {t("cookieBanner.title")}
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                {t("cookieBanner.text")}{" "}
                <Link
                  to={`/${lang}/cookies`}
                  className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
                >
                  {t("cookies.title")}
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 md:flex-shrink-0">
              <button
                data-testid="cookie-reject"
                onClick={rejectAll}
                className="px-4 py-2 rounded-md border border-slate-700 text-slate-200 hover:border-slate-500 transition-colors text-sm"
              >
                {t("cookieBanner.rejectAll")}
              </button>
              <button
                data-testid="cookie-preferences"
                onClick={() => setShowPrefs(true)}
                className="px-4 py-2 rounded-md border border-slate-700 text-slate-200 hover:border-slate-500 transition-colors text-sm"
              >
                {t("cookieBanner.preferences")}
              </button>
              <button
                data-testid="cookie-accept"
                onClick={acceptAll}
                className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
              >
                {t("cookieBanner.acceptAll")}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">
              {t("cookieBanner.preferences")}
            </h3>
            <div className="space-y-3">
              <PrefRow
                title={t("cookieBanner.categories.necessary.title")}
                text={t("cookieBanner.categories.necessary.text")}
                state={t("cookieBanner.categories.necessary.state")}
                disabled
                checked
              />
              <PrefRow
                title={t("cookieBanner.categories.analytics.title")}
                text={t("cookieBanner.categories.analytics.text")}
                checked={consent.analytics}
                onChange={() => toggle("analytics")}
                testId="pref-analytics"
              />
              <PrefRow
                title={t("cookieBanner.categories.marketing.title")}
                text={t("cookieBanner.categories.marketing.text")}
                checked={consent.marketing}
                onChange={() => toggle("marketing")}
                testId="pref-marketing"
              />
              <PrefRow
                title={t("cookieBanner.categories.personalization.title")}
                text={t("cookieBanner.categories.personalization.text")}
                checked={consent.personalization}
                onChange={() => toggle("personalization")}
                testId="pref-personalization"
              />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row justify-end gap-2">
              <button
                data-testid="cookie-reject-prefs"
                onClick={rejectAll}
                className="px-4 py-2 rounded-md border border-slate-700 text-slate-200 hover:border-slate-500 transition-colors text-sm"
              >
                {t("cookieBanner.rejectAll")}
              </button>
              <button
                data-testid="cookie-save-prefs"
                onClick={savePrefs}
                className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
              >
                {t("cookieBanner.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PrefRow({ title, text, state, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 border border-slate-800 rounded-lg p-4">
      <div className="flex-1">
        <div className="text-white font-medium text-sm">{title}</div>
        <p className="text-slate-400 text-xs mt-1 leading-relaxed">{text}</p>
      </div>
      <div className="flex-shrink-0 pt-1">
        {disabled ? (
          <span className="text-xs text-emerald-400 font-mono uppercase">
            {state}
          </span>
        ) : (
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              data-testid={testId}
              checked={!!checked}
              onChange={onChange}
              className="sr-only peer"
            />
            <span className="w-10 h-6 bg-slate-700 rounded-full relative peer-checked:bg-blue-600">
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                  checked ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </span>
          </label>
        )}
      </div>
    </div>
  );
}
